import React from 'react';
import { Popover, ActionIcon, Indicator, Button, Text, Skeleton } from '@mantine/core';
import { IconBell, IconLock } from '@tabler/icons-react';
import { RiskIndicator } from '../risk-indicator/risk-indicator';
import { useI18n } from '../../locales/i18n-context';
import { useRouter } from '../../hooks/use-router';
import { paths } from '../../router/paths';
import { useHighRiskPlanets } from '../../hooks/use-high-risk-planets';
import './alerts-menu.css';

export const AlertsMenu: React.FC = () => {
  const { t } = useI18n();
  const router = useRouter();
  const { highRiskPlanets, loading } = useHighRiskPlanets();

  const renderContent = () => {
    if (loading) {
      return (
        <div className="cb-alerts__loading">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} height={72} mb={8} radius="md" />
          ))}
        </div>
      );
    }

    if (highRiskPlanets.length === 0) {
      return (
        <div className="cb-alerts__empty">
          <Text size="sm" c="dimmed">
            {t('dashboard.noAlerts')}
          </Text>
        </div>
      );
    }

    return highRiskPlanets.slice(0, 5).map(({ planet, inProgressCount }) => (
      <div key={planet.id} className="cb-alerts__item">
        <div className="cb-alerts__item-top">
          <div className="cb-alerts__item-name">{planet.name}</div>
          <RiskIndicator risk="HIGH" inProgressCount={inProgressCount} label={t('risk.high')} />
        </div>
        <div className="cb-alerts__item-info">
          {t('planetDetail.inProgressExposure', { count: inProgressCount })}
        </div>
        <Button
          size="xs"
          variant="light"
          color="red"
          leftSection={<IconLock size={11} />}
          onClick={() => router.push(paths.planet.detail(planet.id))}
          fullWidth
        >
          {t('action.viewDetails')}
        </Button>
      </div>
    ));
  };

  return (
    <Popover width={320} position="bottom-end" shadow="md">
      <Popover.Target>
        <Indicator
          inline
          label={highRiskPlanets.length > 0 ? highRiskPlanets.length : undefined}
          size={16}
          color="red"
          disabled={loading || highRiskPlanets.length === 0}
        >
          <ActionIcon variant="subtle" color="gray" title={t('dashboard.recentAlerts')}>
            <IconBell size={18} />
          </ActionIcon>
        </Indicator>
      </Popover.Target>

      <Popover.Dropdown className="cb-alerts__dropdown">
        <div className="cb-alerts__header">
          <span className="cb-alerts__header-title">{t('dashboard.recentAlerts')}</span>
          {highRiskPlanets.length > 0 && (
            <span className="cb-alerts__badge">
              {highRiskPlanets.length} {t('common.critical')}
            </span>
          )}
        </div>

        <div className="cb-alerts__body">{renderContent()}</div>
      </Popover.Dropdown>
    </Popover>
  );
};
